/**
 * 使用Sky和平行光模拟太阳
 */
import {
  MathUtils,
  Vector3,
  DirectionalLight,
  DirectionalLightHelper,
  CameraHelper,
  Box3,
} from "three";
import { Sky } from "../lib/Sky.js";
import { GUI } from "three/examples/jsm/libs/lil-gui.module.min.js";
export class SkyUtil {
  constructor(scene, renderer, useShadow = false) {
    this.scene = scene;
    this.renderer = renderer;
    this.useShadow = useShadow; // 是否使用阴影

    this.moveSpeed = 2; // sun移动速度
    this.enableMove = false; // 是否允许移动
    this.sunAngle = 45; // 太阳角度
    this.lightIntensity = 2; // 光照强度
    this.maxAngle = 360; // 太阳最大角度;
    this.sunRadius = 500; // 太阳半径

    this._createSky();
    this._createLight();
    //this._createGUI();
  }

  update(dt) {
    if (!this.enableMove) return;
    this.sunAngle += this.moveSpeed * dt;
    this.sunAngle = this.sunAngle % this.maxAngle;
    const phi = MathUtils.degToRad(90 - this.sunAngle); // 随时间变化高度角
    const theta = MathUtils.degToRad(180); // 方位角
    const sun = new Vector3();
    sun.setFromSphericalCoords(this.sunRadius, phi, theta);
    this.skyUniforms["sunPosition"].value.copy(sun);

    if (this.directionalLight) {
      this.directionalLight.position.copy(sun);
      this.directionalLight.lookAt(0, 0, 0);

      // 根据太阳高度调整光照强度
      const intensity = Math.max(
        0,
        Math.sin(MathUtils.degToRad(this.sunAngle))
      ); // 太阳高度越低，光照越弱

      this.directionalLight.intensity = intensity * this.lightIntensity;
    }
  }

  _createLight() {
    this.directionalLight = new DirectionalLight(0xffffff, 1);
    this.scene.add(this.directionalLight);

    const phi = MathUtils.degToRad(90 - this.sunAngle);
    const theta = MathUtils.degToRad(180);

    // 平行光位置对齐sun并且看向场景中心
    const sun = new Vector3();
    sun.setFromSphericalCoords(this.sunRadius, phi, theta);
    this.directionalLight.position.copy(sun);
    this.directionalLight.lookAt(0, 0, 0);
    if (this.useShadow) {
      this.directionalLight.castShadow = true;
      this.directionalLight.shadow.mapSize.width = 2048; // 阴影分辨率
      this.directionalLight.shadow.mapSize.height = 2048;
      this.directionalLight.shadow.camera.near = 0.1;
      this.directionalLight.shadow.camera.far = 1000;
      this.directionalLight.shadow.bias = -0.0005; // 适当增加负偏移
      this.directionalLight.shadow.normalBias = 0.1; // 增加法线偏移
      // 扩展阴影相机的覆盖范围
      this.directionalLight.shadow.camera.left = -500; // 左边界
      this.directionalLight.shadow.camera.right = 500; // 右边界
      this.directionalLight.shadow.camera.top = 500; // 上边界
      this.directionalLight.shadow.camera.bottom = -500; // 下边界

      // 必须调用更新投影矩阵
      this.directionalLight.shadow.camera.updateProjectionMatrix();
    }

    // const helper = new DirectionalLightHelper(
    //   this.directionalLight,
    //   5,
    //   0xff0000
    // );
    // helper.position.y = 5;
    // this.scene.add(helper);

    // 根据太阳高度调整光照强度
    const intensity = Math.max(0, Math.sin(MathUtils.degToRad(this.sunAngle))); // 太阳高度越低，光照越弱
    this.directionalLight.intensity = intensity * this.lightIntensity;
  }

  /**
   * 根据模型包围盒设置阴影参数
   * @param {*} models
   */
  updateShadowMapSize(models) {
    if (!this.useShadow) return;
    // 根据模型包围盒设置阴影参数
    if (models && models.length > 0) {
      // 计算整体包围盒
      const boundingBox = new Box3();
      models.forEach((model) => {
        boundingBox.expandByObject(model);
      });

      // 设置阴影相机参数
      const size = boundingBox.getSize(new Vector3());

      this.directionalLight.shadow.camera.near = 0.1;
      this.directionalLight.shadow.camera.far = size.length() * 3;

      // 根据包围盒尺寸设置阴影范围
      const scale = 1.5; // 缩放系数确保完全覆盖
      this.directionalLight.shadow.camera.left = -size.x * scale;
      this.directionalLight.shadow.camera.right = size.x * scale;
      this.directionalLight.shadow.camera.top = size.z * scale;
      this.directionalLight.shadow.camera.bottom = -size.z * scale;
      // 调整阴影偏移参数防止条纹
      this.directionalLight.shadow.bias = -0.0005; // -0.0015; // 适当增加负偏移
      this.directionalLight.shadow.normalBias = 0.1; //0.08; // 增加法线偏移

      this.directionalLight.shadow.camera.updateProjectionMatrix();

      this.sunRadius = size.length() * 1.5;
      const phi = MathUtils.degToRad(90 - this.sunAngle);
      const theta = MathUtils.degToRad(180);
      const sun = new Vector3();
      sun.setFromSphericalCoords(this.sunRadius, phi, theta);
      this.skyUniforms["sunPosition"].value.copy(sun);
      if (this.directionalLight) {
        this.directionalLight.position.copy(sun);
        this.directionalLight.lookAt(0, 0, 0);
      }

      // const cameraHelper = new CameraHelper(
      //   this.directionalLight.shadow.camera
      // );
      // this.scene.add(cameraHelper);
    }
  }

  _createSky() {
    const sky = new Sky();
    sky.scale.setScalar(450000);

    this.skyUniforms = sky.material.uniforms;
    this.skyUniforms["turbidity"].value = 3.8;
    this.skyUniforms["rayleigh"].value = 0.165;
    this.skyUniforms["mieCoefficient"].value = 0.045;
    this.skyUniforms["mieDirectionalG"].value = 0.7;

    const phi = MathUtils.degToRad(90 - this.sunAngle);
    const theta = MathUtils.degToRad(180);

    const sun = new Vector3();
    sun.setFromSphericalCoords(this.sunRadius, phi, theta);

    this.skyUniforms["sunPosition"].value.copy(sun);

    this.shaderSky = sky;

    this.scene.add(sky);
  }

  _createGUI() {
    const effectController = {
      turbidity: 10,
      rayleigh: 3,
      mieCoefficient: 0.005,
      mieDirectionalG: 0.7,
      elevation: 2,
      azimuth: 180,
      fog: 0.0025,
    };
    const gui = new GUI();

    let guiChanged = () => {
      this.skyUniforms["turbidity"].value = effectController.turbidity;
      this.skyUniforms["rayleigh"].value = effectController.rayleigh;
      this.skyUniforms["mieCoefficient"].value =
        effectController.mieCoefficient;
      this.skyUniforms["mieDirectionalG"].value =
        effectController.mieDirectionalG;

      const phi = MathUtils.degToRad(90 - effectController.elevation);
      const theta = MathUtils.degToRad(effectController.azimuth);

      const sun = new Vector3();
      sun.setFromSphericalCoords(this.sunRadius, phi, theta);

      this.skyUniforms["sunPosition"].value.copy(sun);

      if (this.directionalLight) {
        this.directionalLight.position.copy(sun);
        this.directionalLight.lookAt(0, 0, 0);

        // 根据太阳高度调整光照强度
        const intensity = Math.max(
          0,
          Math.sin(MathUtils.degToRad(this.sunAngle))
        ); // 太阳高度越低，光照越弱
        this.directionalLight.intensity = intensity * this.lightIntensity;
      }

      this.scene.fog.density = effectController.fog;
    };

    gui.add(effectController, "turbidity", 0.0, 20.0, 0.1).onChange(guiChanged);
    gui.add(effectController, "rayleigh", 0.0, 4, 0.001).onChange(guiChanged);
    gui
      .add(effectController, "mieCoefficient", 0.0, 0.1, 0.001)
      .onChange(guiChanged);
    gui
      .add(effectController, "mieDirectionalG", 0.0, 1, 0.001)
      .onChange(guiChanged);
    gui
      .add(effectController, "elevation", 0, this.maxAngle, 0.1)
      .onChange(guiChanged);
    gui.add(effectController, "azimuth", -180, 180, 0.1).onChange(guiChanged);
    gui.add(effectController, "fog", 0, 0.05, 0.001).onChange(guiChanged);

    guiChanged();
  }

  /**
   * 设置太阳角度
   * @param {Number} value 0-1的数值
   */
  setSunAngle(value) {
    this.sunAngle = value * this.maxAngle;
    const phi = MathUtils.degToRad(90 - this.sunAngle);
    const theta = MathUtils.degToRad(180);

    const sun = new Vector3();
    sun.setFromSphericalCoords(this.sunRadius, phi, theta);

    this.skyUniforms["sunPosition"].value.copy(sun);

    if (this.directionalLight) {
      this.directionalLight.position.copy(sun);
      this.directionalLight.lookAt(0, 0, 0);

      // 根据太阳高度调整光照强度
      const intensity = Math.max(
        0,
        Math.sin(MathUtils.degToRad(this.sunAngle))
      ); // 太阳高度越低，光照越弱

      this.directionalLight.intensity = intensity * this.lightIntensity;
    }
  }

  /**
   * 获取太阳角度
   * @returns 0-1的小数
   */
  getSunAngle() {
    return this.sunAngle / this.maxAngle;
  }
}
